import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import './ModuleDetail.css';

const ModuleDetail = () => {
  const { moduleId } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  
  const module = {
    id: moduleId,
    title: 'Building Professional Networks',
    description: 'Learn effective networking strategies and build meaningful professional relationships in your field.',
    progress: 65,
    color: 'orange',
    icon: '🤝',
    category: 'Professional Skills',
    difficulty: 'Intermediate',
    duration: '3 weeks',
    estimatedHours: 12,
    instructor: {
      name: 'Dr. Anjali Patel',
      title: 'Career Development Coach',
      bio: 'Dr. Patel has over 15 years of experience in career coaching and professional development.',
      avatar: null
    },
    sections: [
      {
        id: 1,
        title: 'Introduction to Networking',
        lessons: [
          { id: 1, title: 'What is Professional Networking?', type: 'video', duration: '8 min', completed: true },
          { id: 2, title: 'Why Networking Matters', type: 'reading', duration: '5 min', completed: true },
          { id: 3, title: 'Quiz: Networking Basics', type: 'quiz', duration: '10 min', completed: true }
        ]
      },
      {
        id: 2,
        title: 'Networking Strategies',
        lessons: [
          { id: 4, title: 'Online Networking Tips', type: 'video', duration: '12 min', completed: true },
          { id: 5, title: 'LinkedIn Best Practices', type: 'reading', duration: '8 min', completed: true },
          { id: 6, title: 'In-Person Networking Events', type: 'video', duration: '15 min', completed: false }
        ]
      },
      {
        id: 3,
        title: 'Building Relationships',
        lessons: [
          { id: 7, title: 'Making Meaningful Connections', type: 'video', duration: '10 min', completed: false },
          { id: 8, title: 'Following Up Effectively', type: 'reading', duration: '6 min', completed: false },
          { id: 9, title: 'Maintaining Your Network', type: 'video', duration: '9 min', completed: false },
          { id: 10, title: 'Quiz: Relationship Building', type: 'quiz', duration: '10 min', completed: false }
        ]
      }
    ],
    learningObjectives: [
      'Understand the importance of professional networking in career development',
      'Develop effective online and in-person networking strategies',
      'Build and maintain meaningful professional relationships',
      'Leverage social media platforms for professional growth',
      'Navigate networking events with confidence'
    ],
    resources: [
      { title: 'Networking Strategy Workbook', type: 'PDF', size: '2.5 MB' },
      { title: 'LinkedIn Profile Template', type: 'DOCX', size: '850 KB' },
      { title: 'Email Templates for Follow-ups', type: 'PDF', size: '1.2 MB' },
      { title: 'Networking Events Checklist', type: 'PDF', size: '450 KB' }
    ]
  };

  const completedLessons = module.sections.flatMap(s => s.lessons).filter(l => l.completed).length;
  const totalLessons = module.sections.flatMap(s => s.lessons).length;
  const progressPercentage = Math.round((completedLessons / totalLessons) * 100);

  return (
    <div className="module-detail-page">
      <div className="module-detail-container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/">Dashboard</Link>
          <span className="breadcrumb-separator">›</span>
          <Link to="/modules">Modules</Link>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-current">{module.title}</span>
        </nav>

        {/* Module Header */}
        <div className="module-detail-header">
          <div className="module-header-content">
            <span className="module-detail-icon">{module.icon}</span>
            <div className="module-header-text">
              <div className="module-header-meta">
                <span className="category-badge">{module.category}</span>
                <span className={`difficulty-badge ${module.difficulty.toLowerCase()}`}>
                  {module.difficulty}
                </span>
              </div>
              <h1 className="module-detail-title">{module.title}</h1>
              <p className="module-detail-description">{module.description}</p>
              
              <div className="module-header-stats">
                <div className="header-stat-item">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                  <span>{module.estimatedHours} hours</span>
                </div>
                <div className="header-stat-item">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                  </svg>
                  <span>{totalLessons} lessons</span>
                </div>
                <div className="header-stat-item">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
                  </svg>
                  <span>1,247 learners</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="module-progress-card">
            <div className="progress-card-content">
              <span className="progress-label">Your Progress</span>
              <div className="progress-value">
                <span className="progress-number">{progressPercentage}%</span>
                <span className="progress-lessons">{completedLessons} of {totalLessons} lessons</span>
              </div>
              <div className="progress-bar large">
                <div className="progress-fill" style={{ width: `${progressPercentage}%` }}></div>
              </div>
            </div>
            <button className="continue-btn">Continue Learning</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="module-tabs">
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab-btn ${activeTab === 'curriculum' ? 'active' : ''}`}
            onClick={() => setActiveTab('curriculum')}
          >
            Curriculum
          </button>
          <button 
            className={`tab-btn ${activeTab === 'resources' ? 'active' : ''}`}
            onClick={() => setActiveTab('resources')}
          >
            Resources
          </button>
          <button 
            className={`tab-btn ${activeTab === 'instructor' ? 'active' : ''}`}
            onClick={() => setActiveTab('instructor')}
          >
            Instructor
          </button>
        </div>

        {/* Tab Content */}
        <div className="module-content">
          <div className="content-area">
            {activeTab === 'overview' && (
              <div className="tab-content">
                <section className="content-section">
                  <h2 className="section-heading">What You'll Learn</h2>
                  <ul className="objectives-list">
                    {module.learningObjectives.map((objective, index) => (
                      <li key={index} className="objective-item">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/>
                        </svg>
                        <span>{objective}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <section className="content-section">
                  <h2 className="section-heading">Course Overview</h2>
                  <p className="section-text">
                    This comprehensive module is designed to help you master the art of professional networking.
                    Whether you're just starting your career or looking to expand your professional circle, you'll
                    learn proven strategies for building authentic connections that can advance your career goals.
                  </p>
                  <p className="section-text">
                    Through engaging video lessons, practical exercises, and real-world examples, you'll discover
                    how to network effectively both online and in person. You'll learn how to make memorable first
                    impressions, maintain relationships over time, and leverage your network for mutual benefit.
                  </p>
                </section>
              </div>
            )}

            {activeTab === 'curriculum' && (
              <div className="tab-content">
                {module.sections.map((section) => (
                  <div key={section.id} className="curriculum-section">
                    <div className="section-header">
                      <h3 className="section-title">Section {section.id}: {section.title}</h3>
                      <span className="lesson-count">{section.lessons.length} lessons</span>
                    </div>
                    <div className="lessons-list">
                      {section.lessons.map((lesson) => (
                        <div key={lesson.id} className={`lesson-item ${lesson.completed ? 'completed' : ''}`}>
                          <div className="lesson-left">
                            {lesson.completed ? (
                              <div className="lesson-status completed">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                                </svg>
                              </div>
                            ) : (
                              <div className="lesson-status">
                                {lesson.type === 'video' && '▶️'}
                                {lesson.type === 'reading' && '📖'}
                                {lesson.type === 'quiz' && '📝'}
                              </div>
                            )}
                            <div className="lesson-info">
                              <span className="lesson-title">{lesson.title}</span>
                              <span className="lesson-meta">
                                <span className="lesson-type">{lesson.type}</span>
                              </span>
                            </div>
                          </div>
                          <button className="lesson-play-btn">
                            {lesson.completed ? 'Review' : 'Start'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'resources' && (
              <div className="tab-content">
                <section className="content-section">
                  <h2 className="section-heading">Downloadable Resources</h2>
                  <p className="section-text">
                    Access helpful materials to support your learning journey
                  </p>
                  <div className="resources-list">
                    {module.resources.map((resource, index) => (
                      <div key={index} className="resource-item">
                        <div className="resource-icon">
                          {resource.type === 'PDF' ? '📄' : '📝'}
                        </div>
                        <div className="resource-info">
                          <span className="resource-title">{resource.title}</span>
                          <span className="resource-meta">{resource.type} · {resource.size}</span>
                        </div>
                        <button className="resource-download-btn">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                          </svg>
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'instructor' && (
              <div className="tab-content">
                <section className="content-section">
                  <div className="instructor-card">
                    <div className="instructor-avatar">
                      {module.instructor.avatar ? (
                        <img src={module.instructor.avatar} alt={module.instructor.name} />
                      ) : (
                        <span>{module.instructor.name.split(' ').map(n => n[0]).join('')}</span>
                      )}
                    </div>
                    <div className="instructor-info">
                      <h3 className="instructor-name">{module.instructor.name}</h3>
                      <p className="instructor-title">{module.instructor.title}</p>
                      <p className="instructor-bio">{module.instructor.bio}</p>
                    </div>
                  </div>
                </section>
              </div>
            )}
          </div>

          <aside className="sidebar-area">
            <div className="sidebar-card">
              <h3 className="sidebar-title">Quick Actions</h3>
              <div className="quick-actions">
                <button className="action-btn primary">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polygon points="5 3 19 12 5 21 5 3"/>
                  </svg>
                  Resume Module
                </button>
                <button className="action-btn secondary">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
                  </svg>
                  Share Module
                </button>
                <button className="action-btn secondary">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
                  </svg>
                  Bookmark
                </button>
              </div>
            </div>

            <div className="sidebar-card">
              <h3 className="sidebar-title">Module Features</h3>
              <ul className="features-list">
                <li className="feature-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span>Self-paced learning</span>
                </li>
                <li className="feature-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span>Interactive quizzes</span>
                </li>
                <li className="feature-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span>Downloadable resources</span>
                </li>
                <li className="feature-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span>Certificate upon completion</span>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ModuleDetail;

